import type { AuthUser } from "./types";

export type FswlView = "management" | "staff" | "customer";

/** Mirrors backend REGISTERED_VIEWS — extend when new product views ship. */
export const REGISTERED_VIEWS = ["management", "staff"] as const;

export const ACTIVE_VIEW_STORAGE_KEY = "fswl-active-view";

export const viewLabels: Record<Exclude<FswlView, "customer">, string> = {
  management: "Management",
  staff: "Staff",
};

/** Views the signed-in account may open (from session; not inferred from URL). */
export function allowedViewsForUser(user: AuthUser): Exclude<FswlView, "customer">[] {
  if (user.isAdmin) {
    return [...REGISTERED_VIEWS];
  }

  const fromSession = user.accessibleViews?.filter(
    (view): view is "management" | "staff" => view === "management" || view === "staff",
  );
  if (fromSession?.length) {
    return fromSession;
  }

  const fallback: Exclude<FswlView, "customer">[] = [];
  if (user.administrativeRole === "management") fallback.push("management");
  if (user.administrativeRole === "staff") fallback.push("staff");
  return fallback;
}

export function routeForView(view: Exclude<FswlView, "customer">): string {
  if (view === "staff") return "/staff";
  return "/today";
}

export function persistActiveProductView(view: Exclude<FswlView, "customer">): void {
  try {
    sessionStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, view);
  } catch {
    /* private mode / unavailable */
  }
}

export function readActiveProductView(): Exclude<FswlView, "customer"> | null {
  try {
    const value = sessionStorage.getItem(ACTIVE_VIEW_STORAGE_KEY);
    if (value === "management" || value === "staff") return value;
  } catch {
    /* ignore */
  }
  return null;
}

/** Which product view the current route belongs to (for UI labeling only). */
export function activeViewFromPathname(pathname: string): Exclude<FswlView, "customer"> {
  if (pathname.startsWith("/staff")) return "staff";
  if (pathname.startsWith("/account")) {
    return readActiveProductView() ?? "management";
  }
  return "management";
}

export function viewLabel(view: Exclude<FswlView, "customer">): string {
  return viewLabels[view];
}

export function canAccessView(user: AuthUser, view: Exclude<FswlView, "customer">): boolean {
  return allowedViewsForUser(user).includes(view);
}
